// NAMES: the bodies' names over the sim's view, as DOM over the canvas so a
// name is a real control in the HUD's own font. Which bodies, and where each
// lands, is `bodyLabelSet.ts`; this module draws it, takes the click and keeps
// the switch. It makes no three.js resources: the vectors below are math only.

import * as THREE from 'three';

import { declutterLabels, type LabelCandidate } from '../atlas/labelLayout';
import { LABEL_CLEAR_MARGIN, LABEL_CLEAR_RAMP } from '../atlas/orrery';
import { plainBodyName } from '../hudDvText';
import type { DvMap, DvMapState } from '../sim/dvMap';
import type { BodyPlace } from '../sim/bodies';
import type { WorldVec } from '../world/frame';
import {
  labelAnchor, labelPriority, namedIds, occluderOf, projectBody, type Projected,
} from './bodyLabelSet';

/** CSS px between a disc's top edge and the bottom of its name. */
const GAP_PX = 4;
/** CSS px a pinned name keeps from the window edge. */
const EDGE_PX = 8;
/** A name moves only once it has drifted this far, so a still view writes nothing. */
const MOVE_EPS_PX = 0.5;
/** Under this opacity a name takes no click, so a half-faded one cannot be hit through its neighbour. */
const CLICK_MIN_OPACITY = 0.5;
/** How often the HUD panels' rects are re-read, ms: a rect read is a layout flush. */
const PANEL_RECT_INTERVAL_MS = 250;
const STORE_KEY = 'hud-names';

const TAG_TIPS = {
  TARGET: 'The pilot panel\'s target: the pilot rows and amber marker measure to this body. Change it in the pilot panel.',
  PIN: 'The camera is centered on this body. PIN (P) lets it go.',
  GO: 'The delta-v map\'s trip: GO flies the ship here. Click the name again to clear it.',
} as const;
type Tag = keyof typeof TAG_TIPS;

export type NamesSource = 'button' | 'key';
/** What a click on a name did, for the check and for analytics. */
export type NameClickResult = 'picked' | 'cleared' | 'no_plan';

export interface BodyLabelsInject {
  /** `?labelclickinert=1`: a click on a name does nothing. */
  clickInert?: boolean;
  /** `?labelstyleflat=1`: one look for every name. */
  styleFlat?: boolean;
  /** `?labellocalall=1`: every row in the registry named. */
  localAll?: boolean;
  /** `?labelnodeclutter=1`: every name drawn at full opacity, overlapping or not. */
  noDeclutter?: boolean;
  /** `?labelnopersist=1`: the switch is not written to storage. */
  noPersist?: boolean;
}

export interface BodyLabelsDeps {
  camera: THREE.PerspectiveCamera;
  /** World axes into the bubble's, the frame the camera hangs in. */
  invBubble: THREE.Quaternion;
  /** The camera's own heliocentric metres this frame. */
  cameraWorld: (out: WorldVec) => WorldVec;
  places: () => readonly BodyPlace[];
  frameParentId: () => string | null;
  targetId: () => string | null;
  /** The body the camera pin holds, or null while the pin is off. */
  pinBodyId: () => string | null;
  dvMap: Pick<DvMap, 'state' | 'select' | 'subscribe'>;
  /** Put the delta-v map panel back if it is minimized, so a pick is seen. */
  revealDvMap: () => void;
  /** The element the layer goes into, over the canvas and under the panels. */
  host: HTMLElement;
  defaultOn: boolean;
  inject?: BodyLabelsInject;
}

export interface BodyLabelRead {
  id: string;
  name: string;
  look: 'target' | 'pin' | 'go' | 'plain';
  tags: Tag[];
  pickable: boolean;
  reason: string;
  front: boolean;
  onScreen: boolean;
  occludedBy: string | null;
  panelHidden: boolean;
  opacity: number;
  px: { x: number; y: number };
  discPx: number;
  /** The name's own box, which the declutter reads. */
  box: { left: number; top: number; right: number; bottom: number } | null;
  /** The padded button a press lands on. */
  hit: { left: number; top: number; right: number; bottom: number } | null;
  /** The direction to the body in camera axes, x right, y up, -z forward. */
  cam: { x: number; y: number; z: number };
}

export interface BodyLabelsState {
  on: boolean;
  /** Drawn this frame: on, in cruise, and nothing else has the view. */
  shown: boolean;
  frameParentId: string | null;
  targetId: string | null;
  goId: string | null;
  pinId: string | null;
  named: string[];
  labels: BodyLabelRead[];
  lastClick: { id: string; result: NameClickResult } | null;
  inject: Required<BodyLabelsInject>;
}

export interface BodyLabelsHandle {
  /** One frame: project, declutter and place. `visible` false hides the layer. */
  update(visible: boolean): void;
  setOn(on: boolean, source: NamesSource): void;
  isOn(): boolean;
  /** Is this body's name drawn this frame, so a second name for it is not owed. */
  shows(id: string): boolean;
  state(): BodyLabelsState;
  dispose(): void;
}

interface LabelNode {
  id: string;
  el: HTMLButtonElement;
  text: HTMLSpanElement;
  /** What the node was last painted with, so a still frame rebuilds nothing. */
  key: string;
  textW: number;
  textH: number;
  hitW: number;
  hitH: number;
  measured: boolean;
  x: number;
  y: number;
  opacity: number;
  live: boolean;
}

interface Vec3 { x: number; y: number; z: number }

interface Frame {
  id: string;
  name: string;
  radiusM: number;
  major: boolean;
  p: Projected;
  cam: Vec3;
  look: BodyLabelRead['look'];
  tags: Tag[];
  pickable: boolean;
  reason: string;
  occludedBy: string | null;
  onScreen: boolean;
  anchor: { x: number; bottom: number } | null;
  panelHidden: boolean;
  opacity: number;
}

function loadOn(fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(STORE_KEY);
    return v === 'on' ? true : v === 'off' ? false : fallback;
  } catch {
    return fallback;
  }
}

function saveOn(on: boolean): void {
  try {
    localStorage.setItem(STORE_KEY, on ? 'on' : 'off');
  } catch {
    // Private mode or a full quota: the switch still works for this visit.
  }
}

/** Every visible `[data-widget]` panel's rect: a name under a panel cannot be clicked. */
function readPanelRects(): DOMRect[] {
  const out: DOMRect[] = [];
  for (const el of document.querySelectorAll<HTMLElement>('[data-widget]')) {
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) continue;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) continue;
    out.push(r);
  }
  return out;
}

export function createBodyLabels(deps: BodyLabelsDeps): BodyLabelsHandle {
  const inject: Required<BodyLabelsInject> = {
    clickInert: deps.inject?.clickInert === true,
    styleFlat: deps.inject?.styleFlat === true,
    localAll: deps.inject?.localAll === true,
    noDeclutter: deps.inject?.noDeclutter === true,
    noPersist: deps.inject?.noPersist === true,
  };
  let on = loadOn(deps.defaultOn);
  let shown = false;
  let lastClick: BodyLabelsState['lastClick'] = null;

  const layer = document.createElement('div');
  layer.id = 'body-labels';
  layer.classList.add('hidden');
  const canvas = deps.host.querySelector(':scope > canvas');
  if (canvas) canvas.after(layer);
  else deps.host.prepend(layer);

  const nodes = new Map<string, LabelNode>();
  let frames: Frame[] = [];
  const plainNames = new Map<string, string>();
  let named: string[] = [];
  let namedSet = new Set<string>();
  let lastNamedKey = '';
  let sunId: string | null = null;

  // What the delta-v map can plan, kept off its own change events rather
  // than read every frame.
  let plannable = new Set<string>();
  let skipped = new Map<string, string>();
  let originId: string | null = null;
  let goId: string | null = null;
  let mapReason: string | null = null;
  const reasons = new Map<string, string>();
  function takeMap(s: DvMapState): void {
    plannable = new Set(s.survey?.plans.map((p) => p.destinationId) ?? []);
    skipped = new Map((s.survey?.skipped ?? []).map((k) => [k.id, k.reason]));
    originId = s.survey?.originBodyId ?? null;
    goId = s.selectedId;
    mapReason = s.reason;
    reasons.clear();
  }
  takeMap(deps.dvMap.state());
  const unsubscribe = deps.dvMap.subscribe(takeMap);

  function reasonFor(id: string, name: string): string {
    let r = reasons.get(id);
    if (r !== undefined) return r;
    const why = skipped.get(id);
    r = why !== undefined ? `No trip to ${name}: ${why}`
      : id === originId ? `The delta-v map plans its trips from ${name}, so it has none to it.`
        : mapReason !== null ? `No trip to ${name}: ${mapReason}`
          : `The delta-v map has no trip to ${name} from here.`;
    reasons.set(id, r);
    return r;
  }

  let panels: DOMRect[] = [];
  let panelsReadAt = -Infinity;
  const invalidatePanels = (): void => { panelsReadAt = -Infinity; };
  window.addEventListener('resize', invalidatePanels);

  function onPointerDown(ev: MouseEvent): void {
    // A focused button takes Space, which is the sim's all-stop.
    if ((ev.target as Element | null)?.closest('.body-label')) ev.preventDefault();
  }

  function onClick(ev: MouseEvent): void {
    const el = (ev.target as Element | null)?.closest<HTMLElement>('.body-label');
    const id = el?.dataset['body'];
    if (id === undefined) return;
    const node = nodes.get(id);
    if (node === undefined || !node.live) return;
    if (!plannable.has(id)) {
      lastClick = { id, result: 'no_plan' };
      return;
    }
    if (inject.clickInert) return;
    const clearing = deps.dvMap.state().selectedId === id;
    deps.revealDvMap();
    deps.dvMap.select(clearing ? null : id);
    lastClick = { id, result: clearing ? 'cleared' : 'picked' };
  }
  layer.addEventListener('mousedown', onPointerDown);
  layer.addEventListener('click', onClick);

  function clearNodes(): void {
    for (const n of nodes.values()) n.el.remove();
    nodes.clear();
  }

  function nodeFor(id: string): LabelNode {
    let n = nodes.get(id);
    if (n !== undefined) return n;
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'body-label';
    el.dataset['body'] = id;
    const text = document.createElement('span');
    text.className = 'body-label-text';
    el.append(text);
    layer.append(el);
    n = { id, el, text, key: '', textW: 0, textH: 0, hitW: 0, hitH: 0, measured: false,
      x: Number.NaN, y: Number.NaN, opacity: -1, live: false };
    nodes.set(id, n);
    return n;
  }

  /** The words and the look, written only when they change: on a pick, not on a tick. */
  function paint(n: LabelNode, f: Frame): void {
    const key = `${f.name}|${f.look}|${f.tags.join(',')}|${f.pickable ? 1 : 0}|${f.reason}`;
    if (key === n.key) return;
    n.key = key;
    n.measured = false;
    const look = inject.styleFlat ? 'plain' : f.look;
    n.el.className = `body-label body-label-${look}${f.pickable ? ' body-label-pick' : ''}`;
    n.el.classList.add('hud-tip');
    if (n.live) n.el.classList.add('live');
    n.el.dataset['tooltip'] = f.pickable
      ? (goId === f.id ? `${f.name} is the delta-v map's trip. Click to clear it.`
        : `Click to plan a trip to ${f.name} on the delta-v map.`)
      : f.reason;
    n.el.setAttribute('aria-disabled', f.pickable ? 'false' : 'true');
    const name = document.createElement('span');
    name.className = 'body-label-name';
    name.textContent = f.name;
    const parts: HTMLElement[] = [name];
    for (const tag of f.tags) {
      const t = document.createElement('span');
      t.className = `body-label-tag body-label-tag-${tag.toLowerCase()} hud-tip`;
      t.dataset['tooltip'] = TAG_TIPS[tag];
      t.textContent = tag;
      parts.push(t);
    }
    n.text.replaceChildren(...parts);
  }

  const _camWorld: WorldVec = { x: 0, y: 0, z: 0 };
  const _toCam = new THREE.Quaternion();
  const _v = new THREE.Vector3();

  function update(visible: boolean): void {
    const show = on && visible;
    if (show !== shown) {
      shown = show;
      layer.classList.toggle('hidden', !show);
    }
    if (!show) return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const now = performance.now();
    if (now - panelsReadAt >= PANEL_RECT_INTERVAL_MS) {
      panels = readPanelRects();
      panelsReadAt = now;
    }

    const places = deps.places();
    const frameParentId = deps.frameParentId();
    const targetId = deps.targetId();
    const pinId = deps.pinBodyId();
    const namedKey = `${places.length}|${frameParentId}|${targetId}|${goId}`;
    if (namedKey !== lastNamedKey) {
      lastNamedKey = namedKey;
      named = namedIds(places.map((pl) => pl.row), frameParentId, [targetId, goId], inject.localAll);
      namedSet = new Set(named);
      sunId = places.find((pl) => pl.row.parent === null)?.row.id ?? null;
    }

    deps.cameraWorld(_camWorld);
    const cam = deps.camera;
    _toCam.copy(cam.quaternion).invert().multiply(deps.invBubble);
    const tanHalf = Math.tan((cam.fov * Math.PI) / 360);

    // One projection per row: every row can hide a name behind its disc,
    // named or not.
    const all: Array<{ id: string; p: Projected; place: BodyPlace; cam: Vec3 }> = [];
    const occluders: Array<{ id: string; p: Projected }> = [];
    frames = [];
    for (const place of places) {
      const w = place.world;
      _v.set(w.x - _camWorld.x, w.y - _camWorld.y, w.z - _camWorld.z).applyQuaternion(_toCam);
      const p = projectBody(_v.x, _v.y, _v.z, place.row.radius_m, tanHalf, cam.aspect, width, height);
      const id = place.row.id;
      if (p.front && p.discPx >= 1) occluders.push({ id, p });
      if (!namedSet.has(id)) continue;
      all.push({ id, p, place, cam: { x: _v.x, y: _v.y, z: _v.z } });
    }

    for (const o of all) {
      const row = o.place.row;
      let plain = plainNames.get(o.id);
      if (plain === undefined) {
        plain = plainBodyName(row.display_name);
        plainNames.set(o.id, plain);
      }
      const tags: Tag[] = [];
      if (o.id === targetId) tags.push('TARGET');
      if (o.id === pinId) tags.push('PIN');
      if (o.id === goId) tags.push('GO');
      const look: Frame['look'] = o.id === targetId ? 'target'
        : o.id === pinId ? 'pin' : o.id === goId ? 'go' : 'plain';
      const pickable = plannable.has(o.id);
      const onScreen = o.p.front && o.p.px >= 0 && o.p.px <= width && o.p.py >= 0 && o.p.py <= height;
      const f: Frame = {
        id: o.id, name: plain.toUpperCase(), radiusM: row.radius_m,
        major: row.parent === null || row.parent === sunId,
        p: o.p, cam: o.cam, look, tags, pickable,
        reason: pickable ? '' : reasonFor(o.id, plain),
        occludedBy: occluderOf(o.p, occluders, o.id), onScreen,
        anchor: null, panelHidden: false, opacity: 0,
      };
      frames.push(f);
    }

    // Nodes for every name that can draw; the rest are dropped with their words.
    for (const [id, n] of nodes) {
      if (!namedSet.has(id)) {
        n.el.remove();
        nodes.delete(id);
      }
    }
    const drawn: Array<{ f: Frame; n: LabelNode }> = [];
    for (const f of frames) {
      if (f.occludedBy !== null || !f.p.front) continue;
      const n = nodeFor(f.id);
      paint(n, f);
      drawn.push({ f, n });
    }
    // One layout read for every name whose words changed this frame.
    for (const { n } of drawn) {
      if (n.measured) continue;
      n.textW = n.text.offsetWidth;
      n.textH = n.text.offsetHeight;
      n.hitW = n.el.offsetWidth;
      n.hitH = n.el.offsetHeight;
      n.measured = true;
    }

    const candidates: LabelCandidate[] = [];
    for (const { f, n } of drawn) {
      f.anchor = labelAnchor(f.p, n.textW, n.textH, width, height, GAP_PX, EDGE_PX);
      if (f.anchor === null) continue;
      // The padded button stays whole inside the window, so a name at the edge keeps its full hit area.
      const padY = (n.hitH - n.textH) / 2;
      f.anchor.bottom = Math.max(n.textH + padY, Math.min(height - padY, f.anchor.bottom));
      f.anchor.x = Math.max(n.hitW / 2, Math.min(width - n.hitW / 2, f.anchor.x));
      const left = f.anchor.x - n.textW / 2;
      const top = f.anchor.bottom - n.textH;
      const right = left + n.textW;
      const bottom = f.anchor.bottom;
      // Against the padded button, not the words: a press on the padding must not land on a panel.
      const hitL = f.anchor.x - n.hitW / 2;
      const hitT = f.anchor.bottom - n.textH / 2 - n.hitH / 2;
      f.panelHidden = panels.some((r) => hitL < r.right && hitL + n.hitW > r.left
        && hitT < r.bottom && hitT + n.hitH > r.top);
      if (f.panelHidden) continue;
      candidates.push({
        id: f.id, left, top, right, bottom,
        priority: labelPriority(f.id, f.radiusM, f.major, { targetId, goId, frameParentId }),
        subordinateTo: null, fade: 1,
      });
    }
    const placed = inject.noDeclutter
      ? candidates.map((c) => ({ id: c.id, opacity: 1 }))
      : declutterLabels(candidates, { marginShare: LABEL_CLEAR_MARGIN, rampShare: LABEL_CLEAR_RAMP });
    const opacityOf = new Map(placed.map((p) => [p.id, p.opacity]));

    for (const { f, n } of drawn) {
      f.opacity = opacityOf.get(f.id) ?? 0;
      const opacity = f.opacity < 0.004 ? 0 : f.opacity;
      if (Math.abs(opacity - n.opacity) > 0.01 || (opacity === 0) !== (n.opacity === 0)) {
        n.opacity = opacity;
        n.el.style.opacity = opacity.toFixed(3);
        n.el.style.visibility = opacity === 0 ? 'hidden' : 'visible';
      }
      const live = opacity >= CLICK_MIN_OPACITY;
      if (live !== n.live) {
        n.live = live;
        n.el.classList.toggle('live', live);
      }
      if (f.anchor === null || opacity === 0) continue;
      // The button's box is centred on the name's, so the padding grows the
      // hit area evenly round it and stays out of the box above.
      const x = f.anchor.x - n.hitW / 2;
      const y = f.anchor.bottom - n.textH / 2 - n.hitH / 2;
      if (!(Math.abs(x - n.x) <= MOVE_EPS_PX && Math.abs(y - n.y) <= MOVE_EPS_PX)) {
        n.x = x;
        n.y = y;
        n.el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
      }
    }
    // Hidden behind a disc or behind the eye: the node stays, drawn at nothing.
    for (const f of frames) {
      if (f.occludedBy === null && f.p.front) continue;
      const n = nodes.get(f.id);
      if (n === undefined || n.opacity === 0) continue;
      n.opacity = 0;
      n.live = false;
      n.el.classList.remove('live');
      n.el.style.opacity = '0';
      n.el.style.visibility = 'hidden';
    }
  }

  function setOn(next: boolean, _source: NamesSource): void {
    if (next === on) return;
    on = next;
    if (!inject.noPersist) saveOn(on);
    if (!on) {
      clearNodes();
      frames = [];
      if (shown) {
        shown = false;
        layer.classList.add('hidden');
      }
    }
  }

  function shows(id: string): boolean {
    if (!shown) return false;
    const n = nodes.get(id);
    return n !== undefined && n.opacity >= CLICK_MIN_OPACITY;
  }

  function state(): BodyLabelsState {
    return {
      on,
      shown,
      frameParentId: deps.frameParentId(),
      targetId: deps.targetId(),
      goId,
      pinId: deps.pinBodyId(),
      named: [...named],
      labels: frames.map((f) => {
        const n = nodes.get(f.id);
        const drawn = n !== undefined && f.anchor !== null && !f.panelHidden;
        const left = drawn ? f.anchor!.x - n.textW / 2 : 0;
        return {
          id: f.id, name: f.name, look: f.look, tags: [...f.tags], pickable: f.pickable, reason: f.reason,
          front: f.p.front, onScreen: f.onScreen, occludedBy: f.occludedBy, panelHidden: f.panelHidden,
          opacity: n?.opacity ?? 0,
          px: { x: f.p.px, y: f.p.py }, discPx: f.p.discPx,
          box: drawn ? { left, top: f.anchor!.bottom - n.textH, right: left + n.textW, bottom: f.anchor!.bottom } : null,
          hit: drawn ? { left: n.x, top: n.y, right: n.x + n.hitW, bottom: n.y + n.hitH } : null,
          cam: { x: f.cam.x, y: f.cam.y, z: f.cam.z },
        };
      }),
      lastClick,
      inject: { ...inject },
    };
  }

  return {
    update,
    setOn,
    isOn: () => on,
    shows,
    state,
    dispose(): void {
      unsubscribe();
      window.removeEventListener('resize', invalidatePanels);
      layer.removeEventListener('mousedown', onPointerDown);
      layer.removeEventListener('click', onClick);
      clearNodes();
      layer.remove();
    },
  };
}
